import {
  PayPeriod,
  SalaryDisplayMetadata,
  VacancySalaryFields,
} from 'types/vacancyTypes';

export const DEFAULT_SALARY_CURRENCY = 'EUR';

export const PAY_PERIOD_OPTIONS: { key: PayPeriod; label: string }[] = [
  { key: 'hour', label: 'Hour' },
  { key: 'day', label: 'Day' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

export const CURRENCY_OPTIONS = [
  { key: 'EUR', label: 'EUR' },
  { key: 'USD', label: 'USD' },
  { key: 'GBP', label: 'GBP' },
];

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
};

const SCHEMA_PAY_PERIODS: Record<PayPeriod, string> = {
  hour: 'HOUR',
  day: 'DAY',
  month: 'MONTH',
  year: 'YEAR',
};

const DEFAULT_SALARY_DISPLAY: SalaryDisplayMetadata = {
  language_id: 141,
  locale: 'en-US',
  label: 'Salary',
  from: 'from',
  gross: 'gross',
  pay_period_suffixes: {
    hour: ' / h',
    day: ' / day',
    month: ' / month',
    year: ' / year',
  },
  formatted_value: null,
  formatted_line: null,
};

export type SalaryParseResult =
  | { value: number; error?: undefined }
  | { value: null; error: string };

export type SalaryValidationInput = {
  salary_min?: string | number | null;
  salary_max?: string | number | null;
  currency?: string | null;
  pay_period?: PayPeriod | null;
  salary_note?: string | null;
  rangeEnabled?: boolean;
  mandatory?: boolean;
};

export type SalaryNormalizeInput = SalaryValidationInput & {
  allowFromSalary?: boolean;
};

export type SalaryValidationResult = {
  values: VacancySalaryFields;
  errors: Partial<Record<keyof VacancySalaryFields, string>>;
};

export const getEmptySalaryFields = (): VacancySalaryFields => ({
  salary_min: null,
  salary_max: null,
  currency: null,
  pay_period: null,
  salary_note: null,
});

export const parseSalaryInput = (
  input?: string | number | null
): SalaryParseResult => {
  if (input === null || input === undefined || input === '') {
    return { value: null, error: 'Enter a salary amount.' };
  }

  if (typeof input === 'number') {
    return Number.isInteger(input) && input >= 0
      ? { value: input }
      : { value: null, error: 'Enter a valid salary amount.' };
  }

  const value = input.trim();
  if (!value) return { value: null, error: 'Enter a salary amount.' };

  const validFormat =
    /^\d+(,\d{1,2})?$/.test(value) ||
    /^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(value);

  if (!validFormat) {
    return { value: null, error: 'Use numbers with comma decimals.' };
  }

  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return { value: null, error: 'Enter a valid salary amount.' };
  }

  return { value: Math.round(parsed * 100) };
};

export const centsToMajorUnits = (amount?: number | null): number | null => {
  if (amount === null || amount === undefined) return null;
  return amount / 100;
};

export const centsToInputValue = (amount?: number | null): string => {
  const majorUnits = centsToMajorUnits(amount);
  if (majorUnits === null) return '';

  return Number.isInteger(majorUnits)
    ? String(majorUnits)
    : majorUnits.toFixed(2).replace('.', ',');
};

export const isSalaryComplete = (salary: VacancySalaryFields): boolean =>
  salary.salary_min !== null &&
  salary.salary_min !== undefined &&
  !!salary.currency &&
  !!salary.pay_period;

export const validateSalary = ({
  salary_min,
  salary_max,
  currency,
  pay_period,
  salary_note,
  rangeEnabled = false,
  mandatory = false,
}: SalaryValidationInput): SalaryValidationResult => {
  const errors: SalaryValidationResult['errors'] = {};
  const minResult = parseSalaryInput(salary_min);
  const maxResult =
    rangeEnabled && salary_max !== '' && salary_max !== null
      ? parseSalaryInput(salary_max)
      : undefined;

  if (mandatory && minResult.value === null) {
    errors.salary_min = minResult.error;
  } else if (
    salary_min !== '' &&
    salary_min !== null &&
    salary_min !== undefined &&
    minResult.value === null
  ) {
    errors.salary_min = minResult.error;
  }

  if (rangeEnabled) {
    if (maxResult?.value === null) {
      errors.salary_max = maxResult.error;
    } else if (
      minResult.value !== null &&
      maxResult?.value !== undefined &&
      maxResult.value < minResult.value
    ) {
      errors.salary_max = 'Maximum salary must be greater than minimum salary.';
    }
  }

  if (mandatory && !currency) errors.currency = 'Select a currency.';
  if (mandatory && !pay_period) errors.pay_period = 'Select a pay period.';

  return {
    values: {
      salary_min: minResult.value,
      salary_max: rangeEnabled ? maxResult?.value ?? null : minResult.value,
      currency: currency || null,
      pay_period: pay_period || null,
      salary_note: salary_note?.trim() || null,
    },
    errors,
  };
};

export const normalizeSalaryForSubmit = (
  input: SalaryNormalizeInput
): SalaryValidationResult => {
  const result = validateSalary(input);

  if (result.values.salary_min === null) {
    return {
      ...result,
      values: {
        ...result.values,
        salary_max: null,
        currency: null,
        pay_period: null,
      },
    };
  }

  if (input.rangeEnabled && input.allowFromSalary) return result;

  return {
    ...result,
    values: {
      ...result.values,
      salary_max: result.values.salary_min,
    },
  };
};

export const formatSalaryDisplay = (
  {
    salary_min,
    salary_max,
    currency = DEFAULT_SALARY_CURRENCY,
    pay_period,
    salary_note,
  }: VacancySalaryFields,
  display?: SalaryDisplayMetadata | null,
  options: { includeLabel?: boolean } = {}
): string => {
  if (
    salary_min === null ||
    salary_min === undefined ||
    !currency ||
    !pay_period
  ) {
    return '';
  }

  const salaryDisplay = display ?? DEFAULT_SALARY_DISPLAY;
  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
  const suffix = salaryDisplay.pay_period_suffixes[pay_period];

  if (!suffix) return '';

  const min = formatCurrencyAmount(salary_min, symbol, salaryDisplay.locale);
  const max =
    salary_max !== null && salary_max !== undefined
      ? formatCurrencyAmount(salary_max, symbol, salaryDisplay.locale)
      : null;

  const amount =
    max === null
      ? `${salaryDisplay.from} ${min}`
      : salary_max === salary_min
      ? min
      : `${min}-${max}`;

  const value = `${amount} ${salaryDisplay.gross}${suffix}${
    salary_note ? ` (${salary_note})` : ''
  }`;

  return options.includeLabel ? `${salaryDisplay.label}: ${value}` : value;
};

export const salarySchemaUnitText = (payPeriod?: PayPeriod | null) =>
  payPeriod ? SCHEMA_PAY_PERIODS[payPeriod] : undefined;

export const getSalaryValidationMessages = (error: unknown): string[] => {
  const validation =
    getNestedValue(error, ['graphQLErrors', 0, 'extensions', 'validation']) ??
    getNestedValue(error, ['networkError', 'result', 'errors']) ??
    getNestedValue(error, ['networkError', 'result', 'data', 'errors']) ??
    getNestedValue(error, ['response', 'data', 'errors']);

  if (!validation || typeof validation !== 'object') return [];

  return ['salary_min', 'currency', 'pay_period', 'salary_max']
    .flatMap((field) => {
      const message = (validation as Record<string, unknown>)[field];
      return Array.isArray(message)
        ? message
        : message
        ? [String(message)]
        : [];
    })
    .filter(Boolean);
};

const formatCurrencyAmount = (
  amount: number,
  symbol: string,
  locale: string
): string => {
  const majorUnits = amount / 100;
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: Number.isInteger(majorUnits) ? 0 : 2,
    minimumFractionDigits: 0,
  }).format(majorUnits);

  return `${symbol}${formatted}`;
};

const getNestedValue = (source: unknown, path: (string | number)[]): unknown =>
  path.reduce<unknown>((value, key) => {
    if (value === null || value === undefined) return undefined;
    return (value as Record<string | number, unknown>)[key];
  }, source);
